/* MatchPushPrompt — the highest-converting moment to ask for notification
 * permission on a dating app: right after the first match.
 *
 * UX rules (industry-tested):
 *   1. Only ask if the browser supports push AND permission is still "default"
 *      (never re-ask if the user already said no — that gets the OS to block
 *      the prompt entirely).
 *   2. Only ask ONCE per device, ever. Backed by localStorage.
 *   3. Frame around the human ("hear from {name}") not the tech ("enable push").
 *   4. The "Maybe later" link must be visible — feels respectful, lifts opt-in.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { pushSupported, subscribeToPush } from "@/lib/push";

const ASKED_KEY = "spark_match_push_asked";

/** Module-level guard so the prompt only renders once per session even if
 * Discover re-mounts after navigation. */
export function shouldShowMatchPushPrompt() {
  if (typeof window === "undefined") return false;
  if (!pushSupported()) return false;
  if (Notification.permission !== "default") return false;  // granted OR denied → never ask again
  try {
    if (localStorage.getItem(ASKED_KEY) === "1") return false;
  } catch {
    /* localStorage disabled — show anyway */
  }
  return true;
}

export function markMatchPushPromptAsked() {
  try {
    localStorage.setItem(ASKED_KEY, "1");
  } catch {
    /* ignore */
  }
}

export default function MatchPushPrompt({ matchName, onClose }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success(t("match_push.enabled_toast"));
      markMatchPushPromptAsked();
      onClose();
    } catch (e) {
      // User denied or browser blocked — respect that & don't ask again
      toast.message(t("match_push.denied_toast"));
      markMatchPushPromptAsked();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    markMatchPushPromptAsked();
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] bg-[#16171A]/85 backdrop-blur-md flex items-end sm:items-center justify-center px-4 pb-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      data-testid="match-push-prompt"
    >
      <motion.div
        className="w-full max-w-sm bg-[#1E1F23] rounded-[28px] border border-[#10B981]/30 overflow-hidden p-6"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-[#10B981]/15 border border-[#10B981]/40 flex items-center justify-center">
            <Bell className="w-6 h-6 text-[#10B981]" />
          </div>
        </div>

        <h2 className="font-display text-2xl font-medium tracking-tight text-center text-[#F0E6D2] mb-2">
          {t("match_push.title")}
        </h2>
        <p className="text-sm text-[#A0A0A8] text-center mb-5 leading-relaxed">
          {t("match_push.body", { name: matchName || "them" })}
        </p>

        <button
          onClick={enable}
          disabled={busy}
          className="w-full bg-[#10B981] text-[#0E0F12] rounded-full py-3.5 font-bold tracking-wide active:scale-95 transition flex items-center justify-center gap-2 disabled:opacity-60 mb-2"
          data-testid="match-push-enable-btn"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {t("match_push.enable")}
        </button>

        <button
          onClick={dismiss}
          className="w-full text-sm font-display italic text-[#A0A0A8] underline underline-offset-4 py-2"
          data-testid="match-push-dismiss-btn"
        >
          {t("match_push.maybe_later")}
        </button>
      </motion.div>
    </motion.div>
  );
}
